import {
  InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
  useQuery,
  UseQueryOptions,
} from '@tanstack/react-query';
import {
  api,
  QueryKey,
  ServiceCategoryDto,
  VectorContractsListDto,
  VendorDto,
  VendorListDto,
  VendorSelectListDto,
  VendorsListDto,
} from '@sib/api';
import { DEFAULT_PAGE } from '@sib/api/constants';

const CACHE_TIME = 5000;

export const useVendorsList = (
  organizationId: string,
  query: Parameters<
    typeof api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName
  >[1],
  options?: UseQueryOptions<VendorsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [QueryKey.Resource.Vendors, query, organizationId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorsByOrganizationName(
        organizationId,
        query
      ),
  });

export const useGetVendorsSelectInfiniteList = (
  query: Parameters<
    typeof api.vendors.vendorsControllerGetAllVendorsSelectList
  >[0],
  options?: Partial<
    UseInfiniteQueryOptions<
      VendorSelectListDto,
      Error,
      InfiniteData<VendorSelectListDto>
    >
  >
) =>
  useInfiniteQuery({
    ...options,
    queryKey: [
      QueryKey.Resource.VendorsSelectList,
      QueryKey.Operation.List,
      query,
      QueryKey.Operation.Infinite,
      ...(Array.isArray(options?.queryKey) ? options.queryKey : []),
    ],
    queryFn: async ({ pageParam }) =>
      api.vendors.vendorsControllerGetAllVendorsSelectList({
        ...query,
        page: pageParam as number,
      }),
    //TODO : Fix the type of pageParam after backend type fix
    getNextPageParam: (lastPage: any) => {
      const hasNextPage = lastPage.hasNextPage;
      const nextPage = lastPage.page + 1;

      return hasNextPage ? nextPage : undefined;
    },
    initialPageParam: query?.page || DEFAULT_PAGE,
    placeholderData: keepPreviousData,
  });

export const useInternalPortalVendorsList = (
  query: Parameters<typeof api.internalPortal.vendorsControllerGetVendors>[0],
  options?: UseQueryOptions<VendorListDto>
) =>
  useQuery({
    queryKey: [
      QueryKey.Resource.InternalPortalVendors,
      QueryKey.Operation.List,
      query,
    ],
    queryFn: async () => api.internalPortal.vendorsControllerGetVendors(query),
    ...options,
  });

export const useVendor = (
  vendorId: string,
  options?: UseQueryOptions<VendorDto>
) =>
  useQuery({
    ...options,
    enabled: !!vendorId,
    queryKey: [QueryKey.Resource.Vendors, vendorId],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () => api.vendors.vendorsControllerGetVendorById(vendorId),
  });

export const useVendorServiceCategories = (
  organizationId: string,
  options?: UseQueryOptions<ServiceCategoryDto[], Error, ServiceCategoryDto[]>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId,
    queryKey: [
      QueryKey.Resource.VendorsServiceCategories,
      QueryKey.Operation.List,
      organizationId,
    ],
    queryFn: async () =>
      api.organizations.organizationVendorsControllerGetAllVendorServiceCategories(
        organizationId
      ),
  });

export const useVecotorContracts = (
  organizationId: string,
  vendorId: string,
  query: Parameters<
    typeof api.organizations.vectorContractsControllerGetVectorContracts
  >[2],
  options?: UseQueryOptions<VectorContractsListDto>
) =>
  useQuery({
    ...options,
    enabled: !!organizationId && !!vendorId,
    queryKey: [
      QueryKey.Resource.VectorContracts,
      query,
      organizationId,
      vendorId,
    ],
    gcTime: CACHE_TIME,
    staleTime: CACHE_TIME,
    queryFn: async () =>
      api.organizations.vectorContractsControllerGetVectorContracts(
        organizationId,
        vendorId,
        query
      ),
  });
